import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ExportPDFFilipinoEmploymentContract from './ExportPDFFilipinoEmploymentContract';

// eslint-disable-next-line react/prop-types
const FilipinoEmploymentContract = ({ booking_id }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch existing contract data
  const fetchContractData = async () => {
    if (!booking_id) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`external-forms/filipino-contracts/${booking_id}/`);
      if (response.data) {
        setAgencyInfo({
          agencyAddress: response.data.agency_address || '',
          agencyPhone: response.data.agency_phone || ''
        });
        setFormData({
          employerName: response.data.employer_name || '',
          employerAddress: response.data.employer_address || '',
          employerStatus: response.data.employer_status || '',
          employerContact: response.data.employer_contact || '',
          fpaName: response.data.fpa_name || '',
          fpaAddress: response.data.fpa_address || '',
          fpaContact: response.data.fpa_contact || '',
          hswName: response.data.hsw_name || '',
          hswAddress: response.data.hsw_address || '',
          hswStatus: response.data.hsw_status || '',
          hswContact: response.data.hsw_contact || '',
          hswPassport: response.data.hsw_passport || '',
          hswPassportIssue: response.data.hsw_passport_issue || '',
          praName: response.data.pra_name || '',
          praAddress: response.data.pra_address || '',
          praContact: response.data.pra_contact || '',
          site: response.data.site || 'Singapore',
          duration: response.data.duration || '2 years',
          salary: response.data.salary || 600,
          workHours: response.data.work_hours || 'At least 8 hours rest per day',
          restDay: response.data.rest_day || '1 rest day per week',
          transport: response.data.transport || '',
          accommodation: response.data.accommodation || '',
          medical: response.data.medical || '',
          vacation: response.data.vacation || '',
          insurance: response.data.insurance || '',
          death: response.data.death || '',
          remittance: response.data.remittance || '',
          termination: response.data.termination || '',
          disputes: response.data.disputes || '',
          special: response.data.special || '',
          amendments: response.data.amendments || '',
          forceMajeure: response.data.force_majeure || '',
          others: response.data.others || '',
          hswSignature: response.data.hsw_signature || '',
          employerSignature: response.data.employer_signature || '',
          praSignature: response.data.pra_signature || '',
          fpaSignature: response.data.fpa_signature || '',
          undertakeEmployerName: response.data.undertake_employer_name || '',
          undertakeEmployerAddress: response.data.undertake_employer_address || '',
          employerUndertaking: response.data.employer_undertaking || '',
          fpaOwner: response.data.fpa_owner || '',
          fpaAgency: response.data.fpa_agency || '',
          fpaAgencyAddress: response.data.fpa_agency_address || '',
          fpaUndertaking: response.data.fpa_undertaking || '',
          fpaSignature2: response.data.fpa_signature2 || '',
          fpaStamp: response.data.fpa_stamp || ''
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching contract data:', error);
        setErrors({ fetch: 'Failed to load contract data' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractData();
  }, [booking_id]);
  
  const [agencyInfo, setAgencyInfo] = useState({});

  const [formData, setFormData] = useState({
    employerName: '',
    employerAddress: '',
    employerStatus: '',
    employerContact: '',
    fpaName: '',
    fpaAddress: '',
    fpaContact: '',
    hswName: '',
    hswAddress: '',
    hswStatus: '',
    hswContact: '',
    hswPassport: '',
    hswPassportIssue: '',
    praName: '',
    praAddress: '',
    praContact: '',
    site: 'Singapore',
    duration: '2 years',
    salary: 600,
    workHours: 'At least 8 hours rest per day',
    restDay: '1 rest day per week',
    transport: '',
    accommodation: '',
    medical: '',
    vacation: '',
    insurance: '',
    death: '',
    remittance: '',
    termination: '',
    disputes: '',
    special: '',
    amendments: '',
    forceMajeure: '',
    others: '',
    hswSignature: '',
    employerSignature: '',
    praSignature: '',
    fpaSignature: '',
    undertakeEmployerName: '',
    undertakeEmployerAddress: '',
    employerUndertaking: '',
    fpaOwner: '',
    fpaAgency: '',
    fpaAgencyAddress: '',
    fpaUndertaking: '',
    fpaSignature2: '',
    fpaStamp: ''
  });

  const [result, setResult] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitForm = async () => {
    if (!booking_id) {
      setErrors({ submit: 'Booking ID is required' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const apiData = {
        booking: booking_id,
        employer_name: formData.employerName,
        employer_address: formData.employerAddress,
        employer_status: formData.employerStatus,
        employer_contact: formData.employerContact,
        fpa_name: formData.fpaName,
        fpa_address: formData.fpaAddress,
        fpa_contact: formData.fpaContact,
        hsw_name: formData.hswName,
        hsw_address: formData.hswAddress,
        hsw_status: formData.hswStatus,
        hsw_contact: formData.hswContact,
        hsw_passport: formData.hswPassport,
        hsw_passport_issue: formData.hswPassportIssue || null,
        pra_name: formData.praName,
        pra_address: formData.praAddress,
        pra_contact: formData.praContact,
        site: formData.site,
        duration: formData.duration,
        salary: formData.salary,
        work_hours: formData.workHours,
        rest_day: formData.restDay,
        transport: formData.transport,
        accommodation: formData.accommodation,
        medical: formData.medical,
        vacation: formData.vacation,
        insurance: formData.insurance,
        death: formData.death,
        remittance: formData.remittance,
        termination: formData.termination,
        disputes: formData.disputes,
        special: formData.special,
        amendments: formData.amendments,
        force_majeure: formData.forceMajeure,
        others: formData.others,
        hsw_signature: formData.hswSignature,
        employer_signature: formData.employerSignature,
        pra_signature: formData.praSignature,
        fpa_signature: formData.fpaSignature,
        undertake_employer_name: formData.undertakeEmployerName,
        undertake_employer_address: formData.undertakeEmployerAddress,
        employer_undertaking: formData.employerUndertaking,
        fpa_owner: formData.fpaOwner,
        fpa_agency: formData.fpaAgency,
        fpa_agency_address: formData.fpaAgencyAddress,
        fpa_undertaking: formData.fpaUndertaking,
        fpa_signature2: formData.fpaSignature2,
        fpa_stamp: formData.fpaStamp
      };

      const response = await api.post(`external-forms/filipino-contracts/${booking_id}/`, apiData);
      setResult("Filipino Employment Contract submitted successfully!");
      console.log('Contract submitted:', response.data);
    } catch (error) {
      console.error('Error submitting contract:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        setErrors({ submit: 'Failed to submit contract. Please try again.' });
      }
      setResult("Error submitting contract. Please check the errors above.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employerName: '',
      employerAddress: '',
      employerStatus: '',
      employerContact: '',
      fpaName: '',
      fpaAddress: '',
      fpaContact: '',
      hswName: '',
      hswAddress: '',
      hswStatus: '',
      hswContact: '',
      hswPassport: '',
      hswPassportIssue: '',
      praName: '',
      praAddress: '',
      praContact: '',
      site: 'Singapore',
      duration: '2 years',
      salary: 600,
      workHours: 'At least 8 hours rest per day',
      restDay: '1 rest day per week',
      transport: '',
      accommodation: '',
      medical: '',
      vacation: '',
      insurance: '',
      death: '',
      remittance: '',
      termination: '',
      disputes: '',
      special: '',
      amendments: '',
      forceMajeure: '',
      others: '',
      hswSignature: '',
      employerSignature: '',
      praSignature: '',
      fpaSignature: '',
      undertakeEmployerName: '',
      undertakeEmployerAddress: '',
      employerUndertaking: '',
      fpaOwner: '',
      fpaAgency: '',
      fpaAgencyAddress: '',
      fpaUndertaking: '',
      fpaSignature2: '',
      fpaStamp: ''
    });
    setResult('');
  };

  const handleDelete = async () => {
    if (!booking_id) {
      alert('No booking ID provided for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this Filipino Employment Contract record? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Deleting Filipino Employment Contract for booking_id:', booking_id);
      const response = await api.delete(`external-forms/filipino-contracts/${booking_id}/`);
      console.log('Filipino Employment Contract deleted successfully:', response.data);
      
      // Show success message
      alert('Filipino Employment Contract record deleted successfully!');
      
      // Reset the form after deletion
      resetForm();
      
    } catch (error) {
      console.error('Error deleting Filipino Employment Contract:', error);
      alert('Filipino Employment Contract record deleted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { user } = useAuth();

  const handleDownloadPDF = async () => {
    try {
     

      const doc = (
        <ExportPDFFilipinoEmploymentContract 
          formData={formData} 
          user={user}
          agencyInfo={agencyInfo}
        />
      );
      
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      const currentDate = new Date().toISOString().split('T')[0];
      link.download = `filipino-employment-contract-${booking_id || 'draft'}-${currentDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <>
      <div className="page-wrapper" style={{ marginLeft: '0' }}>
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="content-page-header">
                    <h5>Filipino Employment Contract</h5>
                  </div>
                  <p className="text-muted small mb-3">Standard Employment Contract for Foreign Domestic Helpers from the Philippines</p>

                  {isLoading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">
                        Loading contract data...
                        </span>
                    </div>
                    
                  )}

                  {errors.fetch && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {errors.fetch}
                    </div>
                  )}

                  {errors.submit && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {errors.submit}
                    </div>
                  )}

                  <form id="contractForm">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employer Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Name</label>
                              <input 
                                type="text" 
                                name="employerName"
                                value={formData.employerName}
                                onChange={handleInputChange}
                                className={`form-control ${errors.employer_name ? 'is-invalid' : ''}`}
                              />
                              {errors.employer_name && (
                                <div className="invalid-feedback">
                                  {errors.employer_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Status</label>
                              <input 
                                type="text" 
                                name="employerStatus"
                                value={formData.employerStatus}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="input-block mb-3">
                          <label>Address</label>
                          <textarea 
                            name="employerAddress"
                            value={formData.employerAddress}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Contact Number</label>
                          <input 
                            type="text" 
                            name="employerContact"
                            value={formData.employerContact}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">FPA Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="input-block mb-3">
                          <label>FPA Name</label>
                          <input 
                            type="text" 
                            name="fpaName"
                            value={formData.fpaName}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>FPA Address</label>
                          <textarea 
                            name="fpaAddress"
                            value={formData.fpaAddress}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>FPA Contact</label>
                          <input 
                            type="text" 
                            name="fpaContact"
                            value={formData.fpaContact}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Household Service Worker (HSW) Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>HSW Name</label>
                              <input 
                                type="text" 
                                name="hswName"
                                value={formData.hswName}
                                onChange={handleInputChange}
                                className={`form-control ${errors.hsw_name ? 'is-invalid' : ''}`}
                              />
                              {errors.hsw_name && (
                                <div className="invalid-feedback">
                                  {errors.hsw_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Status</label>
                              <input 
                                type="text" 
                                name="hswStatus"
                                value={formData.hswStatus}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="input-block mb-3">
                          <label>Address</label>
                          <textarea 
                            name="hswAddress"
                            value={formData.hswAddress}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                          />
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Contact Number</label>
                              <input 
                                type="text" 
                                name="hswContact"
                                value={formData.hswContact}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Passport No</label>
                              <input 
                                type="text" 
                                name="hswPassport"
                                value={formData.hswPassport}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="input-block mb-3">
                          <label>Passport Issue Date</label>
                          <input 
                            type="date" 
                            name="hswPassportIssue"
                            value={formData.hswPassportIssue}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">PRA Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="input-block mb-3">
                          <label>PRA Name</label>
                          <input 
                            type="text" 
                            name="praName"
                            value={formData.praName}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>PRA Address</label>
                          <textarea 
                            name="praAddress"
                            value={formData.praAddress}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>PRA Contact</label>
                          <input 
                            type="text" 
                            name="praContact"
                            value={formData.praContact}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employment Terms</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Work Site</label>
                              <input 
                                type="text" 
                                name="site"
                                value={formData.site}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Contract Duration</label>
                              <input 
                                type="text" 
                                name="duration"
                                value={formData.duration}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Monthly Salary (S$)</label>
                              <input 
                                type="number" 
                                name="salary"
                                value={formData.salary}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Work Hours</label>
                              <input 
                                type="text" 
                                name="workHours"
                                value={formData.workHours}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="input-block mb-3">
                          <label>Rest Day</label>
                          <input 
                            type="text" 
                            name="restDay"
                            value={formData.restDay}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Additional Terms</h6>
                      </div>
                      <div className="card-body">
                        <div className="input-block mb-3">
                          <label>Transportation</label>
                          <textarea 
                            name="transport"
                            value={formData.transport}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Accommodation</label>
                          <textarea 
                            name="accommodation"
                            value={formData.accommodation}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Medical Benefits</label>
                          <textarea 
                            name="medical"
                            value={formData.medical}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Vacation Leave</label>
                          <textarea 
                            name="vacation"
                            value={formData.vacation}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Insurance</label>
                          <textarea 
                            name="insurance"
                            value={formData.insurance}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Death Benefits</label>
                          <textarea 
                            name="death"
                            value={formData.death}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Remittance</label>
                          <textarea 
                            name="remittance"
                            value={formData.remittance}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Termination</label>
                          <textarea 
                            name="termination"
                            value={formData.termination}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Disputes</label>
                          <textarea 
                            name="disputes"
                            value={formData.disputes}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Special Provisions</label>
                          <textarea 
                            name="special"
                            value={formData.special}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Amendments</label>
                          <textarea 
                            name="amendments"
                            value={formData.amendments}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Force Majeure</label>
                          <textarea 
                            name="forceMajeure"
                            value={formData.forceMajeure}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>Others</label>
                          <textarea 
                            name="others"
                            value={formData.others}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Signatures</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>HSW Signature</label>
                              <input 
                                type="text" 
                                name="hswSignature"
                                value={formData.hswSignature}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Signature</label>
                              <input 
                                type="text" 
                                name="employerSignature"
                                value={formData.employerSignature}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>PRA Signature</label>
                              <input 
                                type="text" 
                                name="praSignature"
                                value={formData.praSignature}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FPA Signature</label>
                              <input 
                                type="text" 
                                name="fpaSignature"
                                value={formData.fpaSignature}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Undertaking by Employer</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Name</label>
                              <input 
                                type="text" 
                                name="undertakeEmployerName"
                                value={formData.undertakeEmployerName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Address</label>
                              <input 
                                type="text" 
                                name="undertakeEmployerAddress"
                                value={formData.undertakeEmployerAddress}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="input-block mb-3">
                          <label>Employer Undertaking</label>
                          <textarea 
                            name="employerUndertaking"
                            value={formData.employerUndertaking}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="4"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">FPA Undertaking</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FPA Owner</label>
                              <input 
                                type="text" 
                                name="fpaOwner"
                                value={formData.fpaOwner}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FPA Agency</label>
                              <input 
                                type="text" 
                                name="fpaAgency"
                                value={formData.fpaAgency}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="input-block mb-3">
                          <label>FPA Agency Address</label>
                          <textarea 
                            name="fpaAgencyAddress"
                            value={formData.fpaAgencyAddress}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                          />
                        </div>
                        <div className="input-block mb-3">
                          <label>FPA Undertaking</label>
                          <textarea 
                            name="fpaUndertaking"
                            value={formData.fpaUndertaking}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="4"
                          />
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FPA Signature</label>
                              <input 
                                type="text" 
                                name="fpaSignature2"
                                value={formData.fpaSignature2}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FPA Stamp</label>
                              <input 
                                type="text" 
                                name="fpaStamp"
                                value={formData.fpaStamp}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <div className="btn-path text-end">
                          <button type="button" className="btn btn-cancel bg-primary-light me-3" onClick={resetForm} disabled={isSubmitting}>
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger me-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Deleting...' : 'Clean'}
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadPDF}
                             className="btn btn-success me-3"
                            disabled={isSubmitting}
                          >
                            <i className="fe fe-download me-1"></i>
                            Download PDF
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={submitForm}
                            disabled={isSubmitting || isLoading}
                          >
                            {isSubmitting ? (
                              <>
                                <i className="fas fa-spinner fa-spin me-2"></i>
                                Submitting...
                              </>
                            ) : (
                              'Submit'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted small mt-3">{result}</p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilipinoEmploymentContract;
